import { useFileInfo } from "@/api/services/global/file";
import { IconClose, IconDownloadBlue, IconFile, IconSearchBlue } from "@/assets/icons";
import { PreviewModal } from "@/components/elements/FileItem/PreviewModal";
import { IconBox } from "@/components/elements/IconBox";
import { FILE_EXISTED, MIME_TYPES } from "@/constants/file";
import { EMPTY_STR } from "@/constants/string";
import { TJobUploadFile } from "@/features/job/types/JobForm";
import { useDialog } from "@/hooks/useDialog";
import { background, button, primary, state, text } from "@/theme/colors";
import { textEllipsis } from "@/theme/minxin";
import { typography } from "@/theme/typography";
import { TExtendFile } from "@/types/file";
import { showError } from "@/utils/error";
import { downloadFileByUrl, downloadFileFromPath } from "@/utils/export";
import { convertByteToMb } from "@/utils/file";
import { truncateWithEllipsis } from "@/utils/string";
import { Box, Skeleton, Stack, StackProps, Tooltip, Typography, css, styled } from "@mui/material";
import dayjs from "dayjs";
import { FC, useState, useMemo } from "react";

type TFileWithPath = { filePath: string; name?: string };
type TFileList = TExtendFile[] | TFileWithPath[] | TJobUploadFile[]; // Add other file types

type TFileItemProps = {
  onRemove?: () => void;
  errorMsg?: string;
  downloadable?: boolean;
  hideIcon?: boolean;
  enableFileSize?: boolean;
  file?: File;
  filePath?: string;
  title?: string;
  currentFileIndex?: number;
  previewEnabled?: boolean;
  files?: TFileList | null;
  iconDownloadable?: boolean;
  // Optional custom content rendered below the filename row and above the errorMsg block.
  // Used by per-file status UI (e.g. OCR warning spinner / message).
  footer?: React.ReactNode;
  updatedAt?: string | null;
} & StackProps;

const PREVIEWABLE_MIME_TYPES = [...MIME_TYPES.IMAGE, ...MIME_TYPES.TIFF, ...MIME_TYPES.PDF];
export const FileItem: FC<TFileItemProps> = ({ file, filePath, downloadable, previewEnabled, currentFileIndex, files, updatedAt, ...rest }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const handleDownloadFile = async () => {
    if (!downloadable) return;
    try {
      setIsDownloading(true);
      let url = "";
      if (file) {
        url = URL.createObjectURL(file);
        await downloadFileByUrl(file?.name, url);
      }
      if (!file && filePath) {
        downloadFileFromPath(filePath);
      }
    } catch (error) {
      showError(error);
    } finally {
      setIsDownloading(false);
    }
  };

  const { data: infoResult } = useFileInfo(filePath);
  const infoData = infoResult?.data;

  const normalizedFiles = files && Array.isArray(files) ? files : undefined;

  if (file) {
    return (
      <FileItemContent
        fileName={file.name}
        fileSize={file.size}
        downloadable={downloadable}
        isDownloading={isDownloading}
        onDownload={handleDownloadFile}
        currentFileIndex={currentFileIndex}
        filePath={filePath}
        previewEnabled={previewEnabled}
        mimeType={infoData?.mimeType}
        files={normalizedFiles}
        updatedAt={updatedAt}
        {...rest}
      />
    );
  }
  if (filePath) {
    return (
      <FilePathItem
        currentFileIndex={currentFileIndex}
        filePath={filePath}
        isDownloading={isDownloading}
        downloadable={downloadable}
        onDownload={handleDownloadFile}
        previewEnabled={previewEnabled}
        files={normalizedFiles}
        updatedAt={updatedAt}
        {...rest}
      />
    );
  }
  return null;
};

type TFilePathItemProps = {
  filePath: string;
  onDownload: () => void;
  isDownloading: boolean;
  currentFileIndex?: number;
  files?: TFileList;
  previewEnabled?: boolean;
} & TFileItemProps;

const FilePathItem: FC<TFilePathItemProps> = ({ filePath, onDownload, title, currentFileIndex, updatedAt, ...rest }) => {
  if (!filePath || filePath === FILE_EXISTED) return null;
  const { data: infoResult, isLoading } = useFileInfo(filePath);
  const infoData = infoResult?.data;

  if (!infoData && !isLoading) return null;
  const fileName = title && infoData?.name ? `${title}（${infoData.name}）` : infoData?.name || EMPTY_STR.TEXT;
  return (
    <FileItemContent
      isLoading={isLoading}
      fileName={fileName}
      fileSize={infoData?.size || 0}
      currentFileIndex={currentFileIndex}
      onDownload={onDownload}
      mimeType={infoData?.mimeType}
      filePath={filePath}
      updatedAt={updatedAt}
      {...rest}
    />
  );
};

type TFileItemContentProps = {
  onRemove?: () => void;
  onDownload: () => void;
  errorMsg?: string;
  downloadable?: boolean;
  fileName: string;
  fileSize: number;
  isLoading?: boolean;
  isDownloading?: boolean;
  hideIcon?: boolean;
  enableFileSize?: boolean;
  currentFileIndex?: number;
  previewEnabled?: boolean;
  mimeType?: string;
  filePath?: string;
  files?: TFileList;
  iconDownloadable?: boolean;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  updatedAt?: string | null;
} & StackProps;

const FileItemContent: FC<TFileItemContentProps> = ({
  onRemove,
  onDownload,
  errorMsg,
  downloadable = false,
  isLoading = false,
  isDownloading = false,
  fileName,
  fileSize,
  hideIcon = false,
  enableFileSize = false,
  currentFileIndex = 0,
  previewEnabled = false,
  mimeType,
  filePath,
  files = [],
  iconDownloadable = false,
  children,
  footer,
  updatedAt,
  ...rest
}) => {
  const previewModal = useDialog();
  const [currentImageIndex, setCurrentImageIndex] = useState(currentFileIndex);

  const displayFileName = useMemo(() => {
    if (!updatedAt || !fileName) return truncateWithEllipsis(fileName, 80);

    const formattedDate = dayjs(updatedAt).format("YYYYMMDD");
    const lastDotIndex = fileName.lastIndexOf(".");
    const namePart = lastDotIndex !== -1 ? fileName.substring(0, lastDotIndex) : fileName;
    const extPart = lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : "";

    const combinedName = `${namePart}${formattedDate}${extPart}`;

    if (combinedName.length <= 30) {
      return combinedName;
    }

    const maxNameLength = 30 - 3 - formattedDate.length - extPart.length;
    if (maxNameLength > 0) {
      const truncatedName = namePart.substring(0, maxNameLength);
      return `${truncatedName}...${formattedDate}${extPart}`;
    }

    return truncateWithEllipsis(combinedName, 30);
  }, [fileName, updatedAt]);

  const openModalPreview = (index: number) => {
    setCurrentImageIndex(index);
    previewModal.show();
  };

  return (
    <>
      <Stack {...rest} gap={0.5} minWidth={0}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" minWidth={0}>
          <FileItemWrap error={!!errorMsg} downloadable={downloadable && !iconDownloadable} isDownloading={isDownloading} onClick={onDownload}>
            {!hideIcon && <Box sx={{ flexShrink: 0, display: "flex" }}><IconFile fontSize="32px" /></Box>}
            <Tooltip
              arrow
              slotProps={{
                tooltip: {
                  sx: {
                    backgroundColor: background.white,
                    color: text.primary,
                    boxShadow: "0px 2px 8px 0px #00000040",
                  },
                },
                arrow: {
                  sx: {
                    color: background.white,
                  },
                },
              }}
              title={
                <Typography
                  variant="cap12"
                  sx={{
                    backgroundColor: background.white,
                    color: text.primary,
                  }}
                >
                  {fileName}
                </Typography>
              }
            >
              <FileName disableEllipsis={!!updatedAt}>{isLoading ? <Skeleton variant="text" height={32} /> : displayFileName}</FileName>
            </Tooltip>
          </FileItemWrap>
          <Stack direction="row" alignItems="center" gap={1}>
            {!enableFileSize || isLoading ? null : (
              <Typography variant="body14" mr={0.5} whiteSpace="nowrap">
                {convertByteToMb(fileSize).toFixed(2)}MB
              </Typography>
            )}

            {children}

            {onRemove && (
              <IconBox onClick={onRemove}>
                <IconClose />
              </IconBox>
            )}
            <Stack direction="row" alignItems="center" gap={1} sx={{ marginLeft: "16px" }}>
              {previewEnabled && mimeType && PREVIEWABLE_MIME_TYPES.includes(mimeType) && (
                <IconBox sx={{ width: 20, height: 20, padding: 0, flexShrink: 0 }} onClick={() => openModalPreview(currentFileIndex)}>
                  <IconSearchBlue width={20} height={20} color={primary[500]} />
                </IconBox>
              )}
              {iconDownloadable && (
                <IconBox
                  sx={{
                    width: 20,
                    height: 20,
                    padding: 0,
                    flexShrink: 0,
                    cursor: isDownloading ? "not-allowed" : "pointer",
                    opacity: isDownloading ? 0.5 : 1,
                  }}
                  onClick={isDownloading ? undefined : onDownload}
                  disabled={isDownloading}
                >
                  <IconDownloadBlue color={isDownloading ? button.disable : primary[500]} width={20} height={20} />
                </IconBox>
              )}
            </Stack>
          </Stack>
        </Stack>
        {footer}
        {errorMsg && (
          <Typography variant="body14" color={text.error} whiteSpace="pre-line">
            {errorMsg}
          </Typography>
        )}
      </Stack>
      {previewEnabled && files && files.length > 0 && (
        <PreviewModal
          currentIndex={currentImageIndex}
          filePath={filePath || (files[currentImageIndex] as TFileWithPath | TJobUploadFile)?.filePath}
          fileList={files}
          open={previewModal.open}
          onClose={previewModal.cancel}
          setCurrentIndex={setCurrentImageIndex}
        />
      )}
    </>
  );
};

const options = { shouldForwardProp: (propName: string) => !["error", "downloadable", "isDownloading"].includes(propName) };

const FileItemWrap = styled(Box, options)<{ error: boolean; downloadable: boolean; isDownloading: boolean }>`
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 8px;
  margin-right: 4px;
  ${({ error }) => error && FileItemError}
  ${({ downloadable }) => downloadable && FileItemDownload}
  ${({ isDownloading }) => isDownloading && FileItemDownloading}
`;

const FileItemDownloading = css`
  pointer-events: none;
  opacity: 0.8;
`;

const FileItemDownload = css`
  cursor: pointer;
  color: ${state.link_1};
  :hover {
    color: ${state.link_2};
  }
`;

const FileItemError = css`
  &,
  button {
    color: ${text.error};
  }
`;

const FileName = styled(Typography, { shouldForwardProp: (prop) => prop !== "disableEllipsis" })<{ disableEllipsis?: boolean }>`
  ${({ disableEllipsis }) => !disableEllipsis && textEllipsis};
  ${typography.body14};
  flex: 1;
`;
